import { add, differenceInDays, endOfDay, format, isWeekend, startOfDay } from "date-fns"
import { useState } from "react"
import {
  CalendarDateType,
  bankHolidaysInRange,
  getResetDateRange,
  loadDeductedHolidays,
  loadHolidayDates,
  loadHolidayRecords,
} from "../../../constants/calendarDates"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { IBranchSettings } from "../../../utils/useSettings"
import { IHolidayRequestDay } from "./Manage/HolidayRequestsForm"
import { hasHrAccessible } from "./hasHrAccessible"
import { hrAccessibleUsersFilter } from "./hrAccessibleUsersFilter"
export { hasHrAccessible, hrAccessibleUsersFilter }

export function extractTimeFromDate(date: Date | string) {
  date = new Date(date)

  return {
    hour: date.getHours(),
    minute: date.getMinutes(),
  }
}

export function useDeductableDays(userId?: string) {
  const skeem = window.skeem
  const [deductedHolidays, setDeductedHolidays] = useState<{ date: Date; country: string }[]>([])
  useAsyncEffect(async () => {
    const userIdToUser = userId ?? skeem.currentSession.id
    if (!userIdToUser) {
      return
    }
    const user = await skeem.fetch2<{ settings: IBranchSettings }>({
      first: {
        name: "skeem_users",
        attributes: [{ as: "settings", value: { path: ["baseBranch", "settings"] } }],
        filter: { matches: { id: userIdToUser } },
      },
    })
    const deductedHolidays = await loadDeductedHolidays(skeem, {
      useParsedReset: true,
      country: user.settings.hrRegion,
    })
    setDeductedHolidays(deductedHolidays)
  }, [])
  return deductedHolidays
}

export function useHolidayDates(filters?: Parameters<typeof loadHolidayDates>[1]) {
  const skeem = window.skeem
  const [hasLoaded, setHasLoaded] = useState(false)
  const [holidayRecords, setHolidayRecords] = useState<
    {
      date: Date
      id: string
      country: string
      type: CalendarDateType
    }[]
  >([])
  const holidays = holidayRecords.map(x => x.date)

  async function getSettings() {
    const user = await skeem.fetchMe<{ settings: IBranchSettings }>([
      { as: "settings", value: { path: ["baseBranch", "settings"] } },
    ])
    return user.settings
  }

  async function getHolidays() {
    const settings = { ...filters }
    if (!("country" in settings) && !settings.userId) {
      const userSettings = await getSettings()
      settings.country = userSettings.hrRegion ?? ""
    }
    const dates = await loadHolidayRecords(skeem, settings)
    setHolidayRecords(dates ?? [])
    setHasLoaded(true)
  }

  useAsyncEffect(getHolidays, [skeem.currentSession.id, JSON.stringify(filters)])

  function isSameDate(date1: Date, date2: Date) {
    return format(date1, "dd/MM/yyyy") === format(date2, "dd/MM/yyyy")
  }

  function filterDaysJsonByHolidays(days: IHolidayRequestDay[]) {
    return days.filter(
      day => !holidayRecords.some(holiday => isSameDate(holiday.date, new Date(day.date))),
    )
  }
  function sumDaysJSON(days: IHolidayRequestDay[]) {
    return filterDaysJsonByHolidays(days).reduce((count, item) => count + item.value, 0)
  }
  function isHoliday(date: Date) {
    return holidayRecords.find(holiday => isSameDate(holiday.date, date))
  }

  return {
    calculateWorkingDays: (startDate: string | Date, endDate: string | Date) =>
      calculateWorkingDays(startDate, endDate, holidays),
    filterWorkingDays: (startDate: string | Date, endDate: string | Date) =>
      filterWorkingDays(startDate, endDate, holidays),
    filterDaysJsonByHolidays,
    sumDaysJSON,
    isHoliday,
    hasLoaded,
    holidayRecords,
    holidays,
    bankHolidays: holidayRecords.filter(x => x.type === CalendarDateType.BankHoliday),
    forcedHolidays: holidayRecords.filter(x => x.type === CalendarDateType.ForcedHoliday),
  }
}

export function parseBranchResetFilter() {
  const { pastDate, futureDate } = getResetDateRange()
  // check to see if start date is within reset date range
  // or end date is within reset range
  // need to manually calculate the days after this fact and split if crossing reset date
  const filter = {
    or: [
      {
        and: [
          { matches: { startDate: { gte: startOfDay(pastDate) } } },
          { matches: { startDate: { lte: endOfDay(futureDate) } } },
        ].filter(Boolean),
      },
      {
        and: [
          { matches: { endDate: { gte: startOfDay(pastDate) } } },
          { matches: { endDate: { lte: endOfDay(futureDate) } } },
        ].filter(Boolean),
      },
    ],
  }
  return filter
}

export function calculateWorkingDays(
  startDate: string | Date,
  endDate: string | Date,
  bankHolidays: Date[],
) {
  const { pastDate, futureDate } = getResetDateRange()
  const sDate = new Date(startDate)
  const eDate = new Date(endDate)
  let _start, _end
  if (sDate.valueOf() < pastDate.valueOf() && eDate.valueOf() > pastDate.valueOf()) {
    // if the start date is before the pastDate & end date is after the pastDate
    // split and take days after pastDate
    _start = pastDate
    _end = eDate
  } else if (sDate.valueOf() < futureDate.valueOf() && eDate.valueOf() > futureDate.valueOf()) {
    // if the start date is before the futureDate & end date is after the futureDate
    // split and take days before futureDate
    _start = sDate
    _end = futureDate
  } else {
    _start = sDate
    _end = eDate
  }
  return filterWorkingDays(_start, _end, bankHolidays).length
}

export function filterWorkingDays(
  startDate: string | Date,
  endDate: string | Date,
  bankHolidays: Date[],
) {
  const sDate = new Date(startDate)
  const eDate = new Date(endDate)
  if (sDate.valueOf() > eDate.valueOf()) {
    return []
  }
  const days = differenceInDays(eDate, sDate) + 1
  const bankHolidaysRanged = bankHolidaysInRange(sDate, eDate, bankHolidays).map(date =>
    format(date, "yyyy-MM-dd"),
  )
  return new Array(days)
    .fill(0)
    .map((_, index) => add(new Date(sDate), { days: index }))
    .filter(date => {
      const dateKey = format(date, "yyyy-MM-dd")

      return !bankHolidaysRanged.includes(dateKey) && !isWeekend(date)
    })
}
